import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Plus, Pencil, Building2, FolderOpen, ExternalLink, AlertTriangle, Clock, FileWarning } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { PRIORITY_COUNTRIES, HIDDEN_COUNTRIES, flagEmoji } from "@/lib/countries";
import { useAuth } from "@/lib/auth";
import Flag from "@/components/Flag";
import IconAction from "@/components/IconAction";
import ClientGroupCombobox from "@/components/ClientGroupCombobox";
import RemindersPausedBadge from "@/components/RemindersPausedBadge";

const EMPTY = { name: "", group_name: "", address: "", ice: "", country: "", currency: "EUR", billing_type: "HT", is_subcontractor: false, drive_folder_id: "", drive_folder_name: "", payment_delay_days: "" };

/**
 * Iter-99 — Drive folder is now stored from a pasted URL/ID instead of the
 * native Picker (the Picker had Radix interop issues). Accepts:
 *   - https://drive.google.com/drive/folders/<ID>(?...)?
 *   - https://drive.google.com/drive/u/N/folders/<ID>
 *   - <ID> directly (28+ char alphanum string, the canonical Drive id format)
 * Returns the folder id or "" if the input is unparsable.
 */
function parseDriveFolderId(raw) {
  if (!raw) return "";
  const s = String(raw).trim();
  if (!s) return "";
  // Already a bare id? (aligned with backend google_auth.parse_drive_folder_id: {10,})
  if (/^[a-zA-Z0-9_-]{10,}$/.test(s) && !s.includes("/")) return s;
  // Folder URL — match /folders/<id>
  const m = s.match(/\/folders\/([a-zA-Z0-9_-]{10,})/);
  if (m) return m[1];
  // Some flows use ?id=<ID>
  const m2 = s.match(/[?&]id=([a-zA-Z0-9_-]{10,})/);
  if (m2) return m2[1];
  return "";
}

// Iter-85 → moved to /lib/countries.js. Kept re-exports for brevity.

export default function Clients() {
  const [items, setItems] = useState([]);
  const [dialog, setDialog] = useState(null); // 'new' | client object | null
  const [form, setForm] = useState(EMPTY);
  const [formErrors, setFormErrors] = useState({});  // Iter-154: highlight required fields
  const [countries, setCountries] = useState([]); // Iter-84: full ISO country list for the form
  const [showAllCountries, setShowAllCountries] = useState(false); // Iter-85: collapsed by default
  const [countryOpen, setCountryOpen] = useState(false);            // Iter-85: controlled Select.open so we can keep it open after "Autre…"
  const [validateTarget, setValidateTarget] = useState(null);       // Iter-94: CEO validation dialog
  const [validateFolderUrl, setValidateFolderUrl] = useState("");   // Iter-99: paste Drive folder URL
  // Iter-95: filter + sort state for the clients list
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("ALL");          // ALL | ACTIVE | PENDING_VALIDATION
  const [filterContract, setFilterContract] = useState("ALL");      // ALL | WITH | WITHOUT
  const [filterCountry, setFilterCountry] = useState("ALL");        // ALL | <ISO-2>
  const [sortKey, setSortKey] = useState("name");                   // name | country | recent
  const [sortDir, setSortDir] = useState("asc");                    // asc | desc
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();
  const isCEO = user?.role === "CEO";

  const load = () => api.get("/clients").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/holidays/countries").then((r) => setCountries(r.data || [])).catch(() => setCountries([]));
  }, []);

  // Iter-91: when navigated from ClientDetail with `?edit=<client_id>`, open the dialog
  // pre-filled as soon as the client list is loaded. Strip the param after opening so a
  // refresh doesn't re-trigger.
  useEffect(() => {
    const editId = params.get("edit");
    if (!editId || items.length === 0) return;
    const target = items.find((c) => c.client_id === editId);
    if (target) open(target);
    const next = new URLSearchParams(params);
    next.delete("edit");
    setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  // Iter-84: when the user picks a country, auto-set currency + billing_type:
  //   - MA  → MAD + TVA_20 (TTC, Moroccan VAT)
  //   - else → EUR + HT (no VAT, default for international clients)
  // The user can still override after the auto-fill.
  const onCountryChange = (code) => {
    const isMA = code === "MA";
    setForm((prev) => ({
      ...prev,
      country: code,
      currency: isMA ? "MAD" : "EUR",
      billing_type: isMA ? "TVA_20" : "HT",
    }));
  };

  const open = (mode) => {
    if (mode === "new") {
      setForm(EMPTY);
      setDialog("new");
    } else {
      setForm({
        name: mode.name || "",
        group_name: mode.group_name || "",
        address: mode.address || "",
        ice: mode.ice || "",
        country: mode.country || "",
        currency: mode.currency || "EUR",
        billing_type: mode.billing_type || "HT",
        is_subcontractor: !!mode.is_subcontractor,
        drive_folder_id: mode.drive_folder_id || "",
        drive_folder_name: mode.drive_folder_name || "",
        payment_delay_days: mode.payment_delay_days != null ? String(mode.payment_delay_days) : "",
      });
      setDialog(mode);
    }
  };

  const save = async () => {
    // Iter-154: highlight every missing-required field at once + focus the first.
    const missing = {
      name: !form.name.trim(),
      address: !(form.address || "").trim(),
      ice: !(form.ice || "").trim(),
      country: !form.country,
    };
    if (Object.values(missing).some(Boolean)) {
      setFormErrors(missing);
      const firstKey = Object.keys(missing).find((k) => missing[k]);
      const node = document.querySelector(`[data-testid="client-field-${firstKey}"]`);
      if (node) { node.focus?.(); node.scrollIntoView?.({ block: "center", behavior: "smooth" }); }
      const labels = { name: "Name", address: "Address", ice: "ICE", country: "Country" };
      toast.error(`Champs requis manquants : ${Object.keys(missing).filter((k) => missing[k]).map((k) => labels[k]).join(", ")}`);
      return;
    }
    setFormErrors({});
    // Iter-99: CEO drive folder still mandatory at creation, but the field is now
    // a pasted URL/ID — resolve it before validating.
    let resolvedFolderId = "";
    if (isCEO && (form.drive_folder_id || "").trim()) {
      resolvedFolderId = parseDriveFolderId(form.drive_folder_id);
      if (!resolvedFolderId) {
        return toast.error("Lien Drive invalide — collez l'URL du dossier (https://drive.google.com/drive/folders/…) ou son ID");
      }
    }
    if (isCEO && dialog === "new" && !resolvedFolderId) {
      return toast.error("Le dossier Drive est obligatoire pour créer un client (CEO).");
    }
    try {
      // Non-CEO cannot send drive_folder_id — strip it to avoid confusing backend logs
      const payload = { ...form };
      if (isCEO && resolvedFolderId) payload.drive_folder_id = resolvedFolderId;
      if (!isCEO) { delete payload.drive_folder_id; delete payload.drive_folder_name; }
      // Iter-194w — payment_delay_days: parse to int or strip (empty=use mission delay).
      if (payload.payment_delay_days === "" || payload.payment_delay_days == null) {
        payload.payment_delay_days = null;
      } else {
        const n = parseInt(payload.payment_delay_days, 10);
        if (!Number.isFinite(n) || n < 1 || n > 365) {
          toast.error("Délai de paiement client : 1 à 365 jours (ou vide).");
          return;
        }
        payload.payment_delay_days = n;
      }
      if (dialog === "new") {
        const r = await api.post("/clients", payload);
        if (r.data?.status === "PENDING_VALIDATION") {
          toast.success("Client créé — en attente de validation CEO");
        } else {
          toast.success("Client created");
        }
      } else {
        await api.patch(`/clients/${dialog.client_id}`, payload);
        toast.success("Client updated");
      }
      setDialog(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  // Iter-99: Drive folder is now resolved from a pasted URL on the client form.
  // Helper: live-parse to give the user immediate feedback without requiring save.
  const formDriveFolderId = parseDriveFolderId(form.drive_folder_id);

  const submitValidation = async () => {
    const fid = parseDriveFolderId(validateFolderUrl);
    if (!fid) { toast.error("URL ou ID Drive invalide — collez le lien du dossier"); return; }
    try {
      await api.post(`/clients/${validateTarget.client_id}/validate`, {
        drive_folder_id: fid,
      });
      toast.success("Client validé");
      setValidateTarget(null);
      setValidateFolderUrl("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur de validation");
    }
  };

  // Iter-95: derived filtered+sorted list
  const filteredItems = (() => {
    const q = search.trim().toLowerCase();
    let arr = items.filter((c) => {
      if (filterStatus !== "ALL" && (c.status || "ACTIVE") !== filterStatus) return false;
      // Iter-103: clients with purchase_order_only=true count as "WITH" for the
      // contract filter — they're operationally covered, just by PO instead of
      // a master contract. Reduces the false-positive "No contract" filter set.
      const hasContractEffective = !!c.has_contract || !!c.purchase_order_only;
      if (filterContract === "WITH" && !hasContractEffective) return false;
      if (filterContract === "WITHOUT" && hasContractEffective) return false;
      if (filterCountry !== "ALL" && (c.country || "") !== filterCountry) return false;
      if (q) {
        const blob = `${c.name || ""} ${c.group_name || ""} ${c.ice || ""}`.toLowerCase();
        if (!blob.includes(q)) return false;
      }
      return true;
    });
    const dir = sortDir === "desc" ? -1 : 1;
    arr = arr.slice().sort((a, b) => {
      if (sortKey === "name") return (a.name || "").localeCompare(b.name || "") * dir;
      if (sortKey === "country") return (a.country || "ZZ").localeCompare(b.country || "ZZ") * dir;
      if (sortKey === "recent") {
        const ad = a.created_at || "";
        const bd = b.created_at || "";
        return (ad < bd ? -1 : ad > bd ? 1 : 0) * dir;
      }
      return 0;
    });
    return arr;
  })();

  const countriesPresent = Array.from(new Set(items.map((c) => c.country).filter(Boolean))).sort();

  return (
    <div className="p-4 md:p-8 lg:p-12 max-w-[1400px]" data-testid="clients-page">
      <div className="flex flex-wrap gap-3 items-baseline justify-between mb-8 gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-3 font-mono">Delivery</div>
          <h1 className="font-heading text-2xl md:text-4xl font-bold tracking-tight">Clients</h1>
          <p className="text-zinc-500 mt-3 max-w-xl">Your customer base. Address & ICE feed invoice PDFs directly.</p>
        </div>
        <button onClick={() => open("new")} data-testid="new-client-btn" className="inline-flex items-center gap-2 px-5 h-10 bg-zinc-950 text-white text-sm rounded-md hover:bg-zinc-800">
          <Plus size={14} /> New client
        </button>
      </div>

      {/* Iter-95: filter & sort toolbar */}
      <div className="flex flex-wrap items-end gap-3 mb-4 bg-white border border-zinc-200 rounded-lg p-3" data-testid="clients-toolbar">
        <div className="flex-1 min-w-[180px]">
          <Label className="text-[10px] uppercase font-mono text-zinc-500">Search</Label>
          <Input
            data-testid="clients-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Name, group, ICE…"
            className="h-9"
          />
        </div>
        <div className="min-w-[140px]">
          <Label className="text-[10px] uppercase font-mono text-zinc-500">Status</Label>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger data-testid="clients-filter-status" className="h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All statuses</SelectItem>
              <SelectItem value="ACTIVE">Active</SelectItem>
              <SelectItem value="PENDING_VALIDATION">Pending CEO</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-[140px]">
          <Label className="text-[10px] uppercase font-mono text-zinc-500">Contract</Label>
          <Select value={filterContract} onValueChange={setFilterContract}>
            <SelectTrigger data-testid="clients-filter-contract" className="h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All</SelectItem>
              <SelectItem value="WITH">With contract</SelectItem>
              <SelectItem value="WITHOUT">No contract</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-[120px]">
          <Label className="text-[10px] uppercase font-mono text-zinc-500">Country</Label>
          <Select value={filterCountry} onValueChange={setFilterCountry}>
            <SelectTrigger data-testid="clients-filter-country" className="h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All</SelectItem>
              {countriesPresent.map((cc) => (
                <SelectItem key={cc} value={cc}><span className="inline-flex items-center gap-1.5"><Flag code={cc} size={14} alt={false} /> {cc}</span></SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-[140px]">
          <Label className="text-[10px] uppercase font-mono text-zinc-500">Sort by</Label>
          <Select value={sortKey} onValueChange={setSortKey}>
            <SelectTrigger data-testid="clients-sort-key" className="h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="name">Name</SelectItem>
              <SelectItem value="country">Country</SelectItem>
              <SelectItem value="recent">Created date</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <button
          type="button"
          data-testid="clients-sort-dir"
          onClick={() => setSortDir(sortDir === "asc" ? "desc" : "asc")}
          title={`Toggle sort direction (${sortDir})`}
          className="h-9 px-3 border border-zinc-200 rounded-md text-xs font-mono uppercase hover:bg-zinc-50"
        >
          {sortDir === "asc" ? "↑ A→Z" : "↓ Z→A"}
        </button>
        <div className="text-[11px] font-mono text-zinc-500 self-center" data-testid="clients-count">
          {filteredItems.length} / {items.length}
        </div>
      </div>

      <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
        <div className="hidden md:grid grid-cols-12 px-5 py-3 text-[10px] uppercase tracking-[0.2em] text-zinc-500 border-b border-zinc-200 font-mono">
          <div className="col-span-3">Name</div>
          <div className="col-span-2">Group / Holding</div>
          <div className="col-span-1">ICE</div>
          <div className="col-span-1">Country</div>
          <div className="col-span-1">Currency</div>
          <div className="col-span-2">Billing</div>
          <div className="col-span-1">Address</div>
          <div className="col-span-1 text-right">Edit</div>
        </div>
        {items.length === 0 && (
          <div className="px-5 py-16 text-sm text-zinc-500 text-center">No clients yet. Click "New client" to add one.</div>
        )}
        {items.length > 0 && filteredItems.length === 0 && (
          <div className="px-5 py-16 text-sm text-zinc-500 text-center" data-testid="clients-no-match">No client matches the current filters.</div>
        )}
        {filteredItems.map((c) => (
          <div key={c.client_id} onClick={() => navigate(`/clients/${c.client_id}`)} className="block md:grid md:grid-cols-12 md:items-center px-4 md:px-5 py-3 md:py-4 border-b border-zinc-100 last:border-b-0 hover:bg-zinc-50 cursor-pointer" data-testid={`client-row-${c.client_id}`}>
            <div className="col-span-3 flex items-center gap-3 min-w-0">
              <div className="w-8 h-8 rounded-md bg-zinc-100 text-zinc-700 flex items-center justify-center shrink-0"><Building2 size={15} /></div>
              <div className="min-w-0 flex-1">
                <div className="font-medium truncate flex items-center gap-2 flex-wrap">
                  {c.name}
                  {c.is_subcontractor && (
                    <span data-testid={`client-subcontractor-badge-${c.client_id}`} className="px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider font-mono bg-amber-100 text-amber-800">Sub</span>
                  )}
                  {c.status === "PENDING_VALIDATION" && (
                    <span
                      data-testid={`client-pending-badge-${c.client_id}`}
                      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider font-mono bg-amber-100 text-amber-900"
                      title="Client en attente de validation CEO"
                    >
                      <Clock size={10} /> Pending CEO
                    </span>
                  )}
                  {c.status !== "PENDING_VALIDATION" && !c.has_contract && c.purchase_order_only && (
                    <span
                      data-testid={`client-po-only-badge-${c.client_id}`}
                      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider font-mono bg-blue-100 text-blue-900"
                      title="Ce client est facturé sur bon de commande uniquement (pas de contrat cadre)"
                    >
                      BC only
                    </span>
                  )}
                  {c.status !== "PENDING_VALIDATION" && !c.has_contract && !c.purchase_order_only && (
                    <span
                      data-testid={`client-no-contract-badge-${c.client_id}`}
                      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider font-mono bg-orange-100 text-orange-900"
                      title="Aucun contrat cadre déposé — les missions restent marquées 'contrat en attente'"
                    >
                      <FileWarning size={10} /> No contract
                    </span>
                  )}
                  {/* Iter-195p/s — visible pause badge with scope */}
                  <RemindersPausedBadge
                    row={c}
                    kind="client"
                    id={c.client_id}
                    canReactivate={isCEO}
                    allowInitialPause={isCEO}
                    onChanged={load}
                  />
                  {isCEO && !c.has_drive_folder && (
                    <span
                      data-testid={`client-no-drive-badge-${c.client_id}`}
                      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider font-mono bg-red-100 text-red-900"
                      title="Aucun dossier Drive attribué — les uploads locaux ne seront pas synchronisés"
                    >
                      <AlertTriangle size={10} /> Drive missing
                    </span>
                  )}
                </div>
                {/* Mobile-only summary line */}
                <div className="md:hidden mt-1 flex items-center gap-2 text-[11px] font-mono text-zinc-500 truncate">
                  <span className="uppercase">{c.currency || "MAD"}</span>
                  <span>·</span>
                  <span>{c.billing_type === "TVA_20" ? "TTC" : "HT"}</span>
                  {c.group_name ? <><span>·</span><span className="truncate">{c.group_name}</span></> : null}
                </div>
              </div>
              <span className="md:hidden shrink-0">
                <IconAction icon={Pencil} testid={`edit-client-${c.client_id}`} label="Éditer le client" onClick={(e) => { e.stopPropagation(); open(c); }} />
              </span>
            </div>
            <div className="hidden md:block md:col-span-2 text-sm text-zinc-700 truncate">{c.group_name || <span className="text-zinc-400">—</span>}</div>
            <div className="hidden md:block md:col-span-1 text-xs font-mono text-zinc-600 truncate">{c.ice || <span className="text-zinc-400">—</span>}</div>
            <div className="hidden md:block md:col-span-1 text-xs font-mono uppercase text-zinc-700" data-testid={`client-country-${c.client_id}`}>
              {c.country ? <span className="inline-flex items-center gap-1"><Flag code={c.country} size={14} alt={false} />{c.country}</span> : <span className="text-zinc-400">—</span>}
            </div>
            <div className="hidden md:block md:col-span-1 text-xs font-mono uppercase text-zinc-700">{c.currency || "MAD"}</div>
            <div className="hidden md:block md:col-span-2 text-xs font-mono uppercase text-zinc-700">{c.billing_type === "TVA_20" ? "TTC (TVA 20%)" : "HT"}</div>
            <div className="hidden md:block md:col-span-1 text-xs text-zinc-600 truncate">{c.address || <span className="text-zinc-400">—</span>}</div>
            <div className="hidden md:block md:col-span-1 text-right">
              {isCEO && c.status === "PENDING_VALIDATION" ? (
                <IconAction icon={FolderOpen} tone="warning" testid={`validate-client-${c.client_id}`} label="Valider ce client en attribuant un dossier Drive"
                  onClick={(e) => { e.stopPropagation(); setValidateTarget(c); setValidateFolderUrl(""); }} />
              ) : (
                <IconAction icon={Pencil} testid={`edit-client-desktop-${c.client_id}`} label="Éditer le client" onClick={(e) => { e.stopPropagation(); open(c); }} />
              )}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{dialog === "new" ? "New client" : `Edit ${dialog?.name}`}</DialogTitle>
            <DialogDescription>Address and ICE will appear verbatim on the invoice PDFs generated for this client.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label>Name <span className="text-red-600">*</span></Label><Input data-testid="client-field-name" value={form.name} onChange={(e) => { setForm({ ...form, name: e.target.value }); if (formErrors.name) setFormErrors({ ...formErrors, name: false }); }} className={formErrors.name ? "border-red-500 ring-1 ring-red-300" : ""} /></div>
            <div><Label>Group / Holding (optional)</Label><ClientGroupCombobox value={form.group_name} onChange={(v) => setForm({ ...form, group_name: v })} testid="client-field-group" /></div>
            <div><Label>Address <span className="text-red-600">*</span></Label><Textarea data-testid="client-field-address" value={form.address} onChange={(e) => { setForm({ ...form, address: e.target.value }); if (formErrors.address) setFormErrors({ ...formErrors, address: false }); }} placeholder="Street, city, country" className={formErrors.address ? "border-red-500 ring-1 ring-red-300" : ""} /></div>
            <div><Label>ICE <span className="text-red-600">*</span></Label><Input data-testid="client-field-ice" value={form.ice} onChange={(e) => { setForm({ ...form, ice: e.target.value }); if (formErrors.ice) setFormErrors({ ...formErrors, ice: false }); }} placeholder="Tax ID" className={formErrors.ice ? "border-red-500 ring-1 ring-red-300" : ""} /></div>
            <div>
              <Label>Country <span className="text-red-600">*</span></Label>
              <Select
                value={form.country || ""}
                open={countryOpen}
                onOpenChange={(o) => { setCountryOpen(o); if (!o) setShowAllCountries(false); }}
                onValueChange={onCountryChange}
              >
                <SelectTrigger data-testid="client-field-country"><SelectValue placeholder="Select a country" /></SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {(() => {
                    const byCode = Object.fromEntries(countries.map((c) => [c.code, c]));
                    const priority = PRIORITY_COUNTRIES.map((code) => byCode[code]).filter(Boolean);
                    const rest = countries
                      .filter((c) => !PRIORITY_COUNTRIES.includes(c.code) && !HIDDEN_COUNTRIES.has(c.code))
                      .sort((a, b) => a.name.localeCompare(b.name));
                    return (
                      <>
                        {priority.map((c) => (
                          <SelectItem key={c.code} value={c.code} data-testid={`client-country-opt-${c.code}`}>
                            <span className="inline-flex items-center gap-1.5"><Flag code={c.code} size={14} alt={false} /> {c.name} ({c.code})</span>
                          </SelectItem>
                        ))}
                        {!showAllCountries && (
                          <button
                            type="button"
                            data-testid="client-country-show-more"
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowAllCountries(true); }}
                            onPointerDown={(e) => e.stopPropagation()}
                            className="w-full text-left py-1.5 pl-8 pr-2 text-sm text-zinc-500 font-medium hover:bg-zinc-100 rounded-sm focus:outline-none"
                          >
                            Autre…
                          </button>
                        )}
                        {showAllCountries && rest.map((c) => (
                          <SelectItem key={c.code} value={c.code} data-testid={`client-country-opt-${c.code}`}>
                            <span className="inline-flex items-center gap-1.5"><Flag code={c.code} size={14} alt={false} /> {c.name} ({c.code})</span>
                          </SelectItem>
                        ))}
                      </>
                    );
                  })()}
                </SelectContent>
              </Select>
              <p className="text-xs text-zinc-500 mt-1">Selecting <strong>Morocco</strong> auto-sets the currency to <strong>MAD</strong> and billing to <strong>TTC (VAT 20%)</strong>; any other country defaults to <strong>EUR</strong> + <strong>HT</strong>. You can still override below.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Currency</Label>
                <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                  <SelectTrigger data-testid="client-field-currency"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="MAD">MAD</SelectItem>
                    <SelectItem value="EUR">EUR</SelectItem>
                    <SelectItem value="USD">USD</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Billing type</Label>
                <Select value={form.billing_type} onValueChange={(v) => setForm({ ...form, billing_type: v })}>
                  <SelectTrigger data-testid="client-field-billing"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="HT">HT (no VAT)</SelectItem>
                    <SelectItem value="TVA_20">TTC (VAT 20%)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <label className="flex items-center gap-2 cursor-pointer pt-1" data-testid="client-field-subcontractor-wrap">
              <input
                type="checkbox"
                data-testid="client-field-subcontractor"
                checked={!!form.is_subcontractor}
                onChange={(e) => setForm({ ...form, is_subcontractor: e.target.checked })}
                className="w-4 h-4 accent-zinc-900"
              />
              <span className="text-sm">Subcontractor client</span>
              <span className="text-xs text-zinc-500">— gates the CRA-export flow before billing</span>
            </label>
            {/* Iter-194w — Payment delay override (in days). Empty = use mission's billing_delay_months × 30. */}
            <div className="border-t border-zinc-100 pt-3" data-testid="client-field-payment-delay-wrap">
              <Label className="text-sm">Délai de paiement (jours)</Label>
              <Input
                type="number"
                min={1}
                max={365}
                value={form.payment_delay_days ?? ""}
                onChange={(e) => setForm({ ...form, payment_delay_days: e.target.value })}
                data-testid="client-field-payment-delay-days"
                placeholder="Vide = utilise le délai de la mission"
                className="mt-1 max-w-xs"
              />
              <p className="text-xs text-zinc-500 mt-1">
                Override par client (1 à 365 jours). Si renseigné, prime sur le délai défini par mission au moment de l'envoi de la facture.
              </p>
            </div>
            {isCEO && (
              <div className="border-t border-zinc-200 pt-4 mt-2" data-testid="client-field-drive-folder-wrap">
                <Label className="text-sm flex items-center gap-1.5">
                  <FolderOpen size={13} /> Drive — Dossier administratif
                  {dialog === "new" && <span className="text-red-600">*</span>}
                  <span className="text-[10px] font-mono text-zinc-500">CEO-only</span>
                </Label>
                <Input
                  data-testid="client-drive-folder-url"
                  value={form.drive_folder_id || ""}
                  onChange={(e) => setForm({ ...form, drive_folder_id: e.target.value })}
                  placeholder="https://drive.google.com/drive/folders/..."
                  className="mt-1.5"
                />
                {form.drive_folder_id && (
                  <div className="flex items-center gap-2 mt-2 text-xs">
                    {formDriveFolderId ? (
                      <>
                        <span className="text-emerald-700 font-mono truncate" data-testid="client-drive-folder-id-preview">
                          ✓ ID : {formDriveFolderId}
                        </span>
                        <a
                          href={`https://drive.google.com/drive/folders/${formDriveFolderId}`}
                          target="_blank" rel="noopener noreferrer"
                          data-testid="client-drive-folder-open"
                          className="p-1 text-zinc-500 hover:text-emerald-700"
                          title="Ouvrir le dossier sur Drive"
                        >
                          <ExternalLink size={12} />
                        </a>
                      </>
                    ) : (
                      <span className="text-red-600 font-mono">⚠ Lien invalide — l'URL doit contenir <code>/folders/&lt;ID&gt;</code></span>
                    )}
                  </div>
                )}
                <p className="text-[10px] text-zinc-500 mt-1.5">
                  Ouvrez le dossier dans Google Drive, copiez l'URL depuis la barre d'adresse, et collez-la ici.
                  Ce dossier reste <strong>invisible pour les autres utilisateurs</strong>.
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <button onClick={() => setDialog(null)} className="px-4 h-10 text-sm hover:bg-zinc-100 rounded-md">Cancel</button>
            <button onClick={save} data-testid="client-save" className="px-4 h-10 bg-zinc-950 text-white text-sm rounded-md">{dialog === "new" ? "Create" : "Save changes"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter-94: CEO validation dialog for PENDING clients */}
      <Dialog open={!!validateTarget} onOpenChange={(o) => !o && setValidateTarget(null)}>
        <DialogContent data-testid="client-validate-dialog">
          <DialogHeader>
            <DialogTitle>Valider « {validateTarget?.name} »</DialogTitle>
            <DialogDescription>
              Collez le lien Google Drive du dossier <strong>partagé</strong> de ce client.
              Le lien restera invisible pour les autres utilisateurs. Lors de la validation,
              la plateforme créera automatiquement <strong>3 sous-dossiers</strong> :
              <span className="font-mono text-[11px] block mt-1 ml-2 text-zinc-700">
                · 001-Devis · 002-BDC · 003-CRA
              </span>
            </DialogDescription>
          </DialogHeader>
          <div className="py-2 space-y-3">
            <div>
              <Label className="text-xs">Lien Drive du dossier</Label>
              <Input
                data-testid="client-validate-folder-url"
                value={validateFolderUrl}
                onChange={(e) => setValidateFolderUrl(e.target.value)}
                placeholder="https://drive.google.com/drive/folders/..."
                autoFocus
              />
              {validateFolderUrl && (
                parseDriveFolderId(validateFolderUrl) ? (
                  <p className="text-[11px] text-emerald-700 font-mono mt-1" data-testid="client-validate-folder-preview">
                    ✓ ID : {parseDriveFolderId(validateFolderUrl)}
                  </p>
                ) : (
                  <p className="text-[11px] text-red-600 font-mono mt-1">
                    ⚠ Lien invalide — l'URL doit contenir <code>/folders/&lt;ID&gt;</code>
                  </p>
                )
              )}
              <p className="text-[10px] text-zinc-500 mt-1.5">
                Ouvrez le dossier dans Google Drive, copiez l'URL depuis la barre d'adresse, et collez-la ici.
              </p>
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setValidateTarget(null)} className="px-4 h-10 text-sm hover:bg-zinc-100 rounded-md">Annuler</button>
            <button
              onClick={submitValidation}
              disabled={!parseDriveFolderId(validateFolderUrl)}
              data-testid="client-validate-submit"
              className="px-4 h-10 bg-amber-600 text-white text-sm rounded-md disabled:opacity-50 hover:bg-amber-700"
            >
              Valider ce client
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
